import { Box, Collapse, IconButton, Stack, TableCell, TableRow, Typography } from "@mui/material"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import Iconify from "minimals-template/components/Iconify"
import { useState } from "react"
import { SelectedTask } from "./components/selected-task"
import { SimilarityResultSelectedTask } from "./components/similarity-result-selected-task"
import { Classification } from "routes/schedule/tasks/classification"
import { Bound } from "lib/@components/binding/Bound"
import { Time } from "routes/schedule/tasks/time"
import { Frequency } from "routes/schedule/tasks/frequency"
import { SkillSet } from "routes/schedule/tasks/skill-set"
import { BlockClicks } from "lib/@components/block-clicks"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { CompareTasks } from "./components/compare-tasks"
import { CircularProgressPercentage } from "minimals-template/components/CircularProgressPercentage"
import { grey } from "@mui/material/colors"
import { createSlot } from "lib/@components/slot/create-slot"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { useDemand } from "lib/authorization/use-demand"
import { AUTHORING_EDIT_SCHEDULES_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"
export const TaskSimilarityItemCell = createSlot("TaskSimilarityItemCell")

export function TaskSimilarityItem({ item, cellPlugsWidth = 60, selectable = true }) {
    const [showMore, setShowMore] = useState(false)
    const { sourceTask, projectGroup, selectionDisabled, usesServerSelection } = useBoundContext()
    const canEditSchedule = useDemand(AUTHORING_EDIT_SCHEDULES_DEMAND)
    const cellPlugs = TaskSimilarityItemCell.useSlot()

    if (!item?.task) return null

    const { task, fullStrength, scheduleTitle, scheduleCode } = item
    const { classification, title, taskId, minutes } = task
    const strength = Math.round(fullStrength * 100)
    const isLocked = Boolean(item?.lock?.locked || item?.lock?.isLocked)
    const selectionCheckbox = buildSelectionCheckbox()
    const lockIcon = isLocked ? (
        <Iconify data-cy="locked-similar-task" icon="mdi:lock" color={grey[500]} width={16} />
    ) : null
    const rowActions = canEditSchedule && cellPlugs?.length > 0 ? <TaskSimilarityItemCell.Slot /> : null

    return (
        <Bound targetId={item.id} task={task} target={task}>
            <TableRow
                onClick={() => setShowMore((show) => !show)}
                data-cy={`task-item-${taskId}`}
                sx={{ cursor: "pointer", opacity: isLocked ? 0.5 : 1 }}
                hover
            >
                <If truthy={selectable}>
                    <TableCell
                        align="center"
                        sx={{ maxWidth: { xs: 150, md: 300 }, whiteSpace: "noWrap", py: 0, pl: 0 }}
                    >
                        <BlockClicks sx={{ justifyContent: "center" }}>{selectionCheckbox || lockIcon}</BlockClicks>
                    </TableCell>
                </If>
                <TableCell sx={{ maxWidth: { xs: 150, md: 300 }, whiteSpace: "noWrap", py: 0, pl: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Classification classification={classification} />
                        <TruncatedTypography
                            sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                flexGrow: 1,
                            }}
                        >
                            {title}
                        </TruncatedTypography>
                    </Stack>
                </TableCell>
                <TableCell sx={{ maxWidth: { xs: 150, md: 300 }, py: 0 }}>
                    <TruncatedTypography noWrap>{scheduleTitle}</TruncatedTypography>
                    <TruncatedTypography noWrap variant="body2" color={grey[600]}>
                        #{scheduleCode}
                    </TruncatedTypography>
                    <If value={isLocked && item?.lock?.projectName}>
                        <TruncatedTypography data-cy="locked-work-package" noWrap variant="caption" color={grey[600]}>
                            Locked in {item?.lock?.projectName}
                        </TruncatedTypography>
                    </If>
                </TableCell>
                <Bound
                    timeLabelSx={{ textAlign: "center" }}
                    minHeightBoxSx={{ justifyContent: "center", marginRight: 1 }}
                >
                    <TableCell sx={{ width: 100, maxWidth: 100, whiteSpace: "noWrap", py: 0 }} align="center">
                        <If truthy={minutes}>
                            <Time />
                            <Else>
                                <Typography>-</Typography>
                            </Else>
                        </If>
                    </TableCell>
                    <TableCell sx={{ width: 100, maxWidth: 100, whiteSpace: "noWrap", py: 0 }} align="center">
                        <Frequency labelSx={{ textAlign: "center" }} />
                    </TableCell>
                </Bound>
                <TableCell sx={{ width: 80, maxWidth: 80, whiteSpace: "noWrap", py: 1 }} align="center">
                    <SkillSet sx={{ marginLeft: 0 }} />
                </TableCell>
                <TableCell sx={{ width: 150, maxWidth: 150, whiteSpace: "noWrap", py: 0, pr: 0, pl: 0 }} align="center">
                    <Box data-cy="task-match-score" display={"flex"} alignItems="center" justifyContent={"center"}>
                        <CircularProgressPercentage value={strength} color={getStrengthColor(70, 100, strength)} />
                    </Box>
                </TableCell>
                <TableCell sx={{ width: 44, maxWidth: 44, whiteSpace: "noWrap", p: 0 }} align="center">
                    <IconButton
                        aria-label={showMore ? "Hide similar task details" : "Show similar task details"}
                        data-cy="show-similar-task-details"
                    >
                        <Iconify icon={showMore ? "mingcute:up-fill" : "mingcute:down-fill"} />
                    </IconButton>
                </TableCell>
                <If truthy={rowActions}>
                    <TableCell sx={{ width: cellPlugsWidth, maxWidth: cellPlugsWidth, whiteSpace: "noWrap", py: 0 }}>
                        {rowActions}
                    </TableCell>
                </If>
            </TableRow>
            <TableRow>
                <TableCell colSpan={selectable ? 8 : 7} sx={{ p: 0 }}>
                    <Box sx={{ overflow: "hidden" }}>
                        <Collapse in={showMore} mountOnEnter sx={{ width: 1 }}>
                            <Bound leftType="original" rightType="similar" taskHeader={true}>
                                <CompareTasks sourceTask={sourceTask} similarExtendedTask={item} />
                            </Bound>
                        </Collapse>
                    </Box>
                </TableCell>
            </TableRow>
        </Bound>
    )

    function buildSelectionCheckbox() {
        if (!canEditSchedule || selectionDisabled || isLocked || projectGroup?.readOnly) return null
        if (usesServerSelection) return <SimilarityResultSelectedTask task={item} />
        return <SelectedTask task={item} />
    }
}

function getStrengthColor(min, max, strength) {
    if (strength === 100) return "success"

    const colors = ["error", "warning", "primary"]
    const range = max - min
    const increment = range / colors.length
    const colorThresholds = Array.from({ length: colors.length }).map((_, i) => min + increment * (i + 1))
    const colorIndex = colorThresholds.findIndex((threshold) => strength < threshold)

    return colorIndex !== -1 ? colors[colorIndex] : "primary"
}
